import React, { useState } from "react";
import {
    ArrowLeft,
    Save,
    X,
    Plus,
    Trash2,
} from "lucide-react";
import { Button } from "../../components/Button";
import FormField from "../../components/ui/FormField";

const ClientForm = ({
    clientData = null,
    onSave,
    onCancel,
    isEditing = false
}) => {
    const [formData, setFormData] = useState(
        clientData || {
            accountName: "",
            accountOwner: "",
            email: "",
            fax: "",
            status: "Active",
            priority: "Medium",
            isStartup: false,
            onboardingStatus: "Not Started",
            contactDetails: {
                designation: "",
                phone: "",
                alternatePhone: "",
            },
            businessInformation: {
                businessType: "",
                annualRevenue: 0,
                gstRegistrations: [],
                locations: [],
            },
            tdsDetails: {
                tan: "",
                authorizedSignatory: "",
                signatoryPAN: "",
                signatoryMobile: "",
                signatoryEmail: "",
                deductions: [],
            },
            workflows: [],
            tasks: [],
            attachments: [],
            compliance: 0,
            lastActivity: "",
            assignedTo: "",
        }
    );

    const [activeSection, setActiveSection] = useState("basic");

    const handleChange = (field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const handleGstChange = (index, field, value) => {
        const updatedGst = [...formData.businessInformation.gstRegistrations];
        updatedGst[index] = {
            ...updatedGst[index],
            [field]: value
        };

        setFormData(prev => ({
            ...prev,
            businessInformation: {
                ...prev.businessInformation,
                gstRegistrations: updatedGst
            }
        }));
    };

    const addGstRegistration = () => {
        setFormData(prev => ({
            ...prev,
            businessInformation: {
                ...prev.businessInformation,
                gstRegistrations: [
                    ...prev.businessInformation.gstRegistrations,
                    {
                        id: Date.now(),
                        gstin: "",
                        registrationType: "Regular",
                        filingFrequency: "Monthly",
                        state: "",
                        nextFilingDate: "",
                        lastFiledDate: "",
                        activeStatus: true
                    }
                ]
            }
        }));
    };

    const removeGstRegistration = (index) => {
        const updatedGst = [...formData.businessInformation.gstRegistrations];
        updatedGst.splice(index, 1);

        setFormData(prev => ({
            ...prev,
            businessInformation: {
                ...prev.businessInformation,
                gstRegistrations: updatedGst
            }
        }));
    };

    const handleLocationChange = (index, field, value) => {
        const updatedLocations = [...formData.businessInformation.locations];
        updatedLocations[index] = {
            ...updatedLocations[index],
            [field]: value
        };

        setFormData(prev => ({
            ...prev,
            businessInformation: {
                ...prev.businessInformation,
                locations: updatedLocations
            }
        }));
    };

    const addLocation = () => {
        setFormData(prev => ({
            ...prev,
            businessInformation: {
                ...prev.businessInformation,
                locations: [
                    ...prev.businessInformation.locations,
                    {
                        id: Date.now(),
                        address: "",
                        city: "",
                        state: "",
                        pincode: "",
                        isHeadOffice: false
                    }
                ]
            }
        }));
    };

    const removeLocation = (index) => {
        const updatedLocations = [...formData.businessInformation.locations];
        updatedLocations.splice(index, 1);

        setFormData(prev => ({
            ...prev,
            businessInformation: {
                ...prev.businessInformation,
                locations: updatedLocations
            }
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={onCancel}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </button>
                        <h1 className="text-2xl font-bold text-gray-900">
                            {isEditing ? "Edit Client" : "Add New Client"}
                        </h1>
                    </div>
                    <div className="flex space-x-3">
                        <Button
                            variant="outline"
                            onClick={onCancel}
                            icon={<X className="h-4 w-4" />}
                            label="Cancel"
                        />
                        <Button
                            onClick={handleSubmit}
                            icon={<Save className="h-4 w-4" />}
                            label="Save Client"
                        />
                    </div>
                </div>

                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        {[
                            { id: "basic", label: "Basic Information" },
                            { id: "contact", label: "Contact Details" },
                            { id: "business", label: "Business Information" },
                            { id: "gst", label: "GST Details" },
                            { id: "tds", label: "TDS Details" },
                            { id: "locations", label: "Locations" },
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveSection(tab.id)}
                                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeSection === tab.id
                                    ? "border-blue-500 text-blue-600"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {activeSection === "basic" && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-6">Basic Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField
                                label="Account Name"
                                value={formData.accountName}
                                onChange={(value) => handleChange("accountName", value)}
                                required={true}
                                placeholder="Enter account name"
                            />

                            <FormField
                                label="Account Owner"
                                value={formData.accountOwner}
                                onChange={(value) => handleChange("accountOwner", value)}
                                required={true}
                                placeholder="Enter account owner name"
                            />

                            <FormField
                                label="Email"
                                type="email"
                                value={formData.email}
                                onChange={(value) => handleChange("email", value)}
                                required={true}
                                placeholder="Enter email address"
                            />

                            <FormField
                                label="Phone"
                                type="number"
                                value={formData.fax}
                                onChange={(value) => handleChange("fax", value)}
                                required={true}
                                placeholder="Enter phone number"
                            />

                            <FormField
                                label="Status"
                                type="dropdown"
                                value={formData.status}
                                onChange={(value) => handleChange("status", value)}
                                options={["Active", "Inactive", "Pending", "Suspended"]}
                            />

                            <FormField
                                label="Priority"
                                type="dropdown"
                                value={formData.priority}
                                onChange={(value) => handleChange("priority", value)}
                                options={["Low", "Medium", "High", "Critical"]}
                            />

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="isStartup"
                                    checked={formData.isStartup}
                                    onChange={(e) => handleChange("isStartup", e.target.checked)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="isStartup" className="ml-2 block text-sm text-gray-900">
                                    Is Startup
                                </label>
                            </div>

                            {formData.isStartup && (
                                <FormField
                                    label="Onboarding Status"
                                    type="dropdown"
                                    value={formData.onboardingStatus}
                                    onChange={(value) => handleChange("onboardingStatus", value)}
                                    options={["Not Started", "In Progress", "Complete"]}
                                />
                            )}
                        </div>
                    </div>
                )}

                {activeSection === "contact" && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-6">Contact Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField
                                label="Designation"
                                value={formData.contactDetails.designation}
                                onChange={(value) => handleChange("contactDetails.designation", value)}
                                placeholder="Enter designation"
                            />

                            <FormField
                                label="Phone"
                                type="number"
                                value={formData.contactDetails.phone}
                                onChange={(value) => handleChange("contactDetails.phone", value)}
                                placeholder="Enter phone number"
                            />

                            <FormField
                                label="Alternate Phone"
                                type="number"
                                value={formData.contactDetails.alternatePhone}
                                onChange={(value) => handleChange("contactDetails.alternatePhone", value)}
                                placeholder="Enter alternate phone number"
                            />
                        </div>
                    </div>
                )}

                {activeSection === "business" && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-6">Business Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField
                                label="Business Type"
                                type="dropdown"
                                value={formData.businessInformation.businessType}
                                onChange={(value) => handleChange("businessInformation.businessType", value)}
                                options={[
                                    "Proprietorship",
                                    "Partnership",
                                    "LLP",
                                    "Private Limited",
                                    "Public Limited",
                                    "Other"
                                ]}
                            />

                            <FormField
                                label="Annual Revenue (₹)"
                                type="number"
                                value={formData.businessInformation.annualRevenue}
                                onChange={(value) => handleChange("businessInformation.annualRevenue", value)}
                                placeholder="Enter annual revenue"
                            />
                        </div>
                    </div>
                )}

                {/* GST Details Section */}
                {activeSection === "gst" && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-gray-900">GST Registrations</h3>
                            <Button
                                onClick={addGstRegistration}
                                icon={<Plus className="h-4 w-4" />}
                                label="Add GST"
                            />
                        </div>

                        {formData.businessInformation.gstRegistrations.length === 0 ? (
                            <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg">
                                <p className="text-gray-500">No GST registrations added yet.</p>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {formData.businessInformation.gstRegistrations.map((gst, index) => (
                                    <div key={gst.id} className="border border-gray-200 rounded-lg p-6 relative">
                                        <button
                                            type="button"
                                            onClick={() => removeGstRegistration(index)}
                                            className="absolute top-4 right-4 text-red-500 hover:text-red-700"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>

                                        <h4 className="text-md font-medium text-gray-900 mb-4">GST Registration #{index + 1}</h4>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <FormField
                                                label="GSTIN"
                                                type="gst"
                                                value={gst.gstin}
                                                onChange={(value) => handleGstChange(index, "gstin", value)}
                                                placeholder="Enter GSTIN"
                                            />

                                            <FormField
                                                label="Registration Type"
                                                type="dropdown"
                                                value={gst.registrationType}
                                                onChange={(value) => handleGstChange(index, "registrationType", value)}
                                                options={["Regular", "Composition", "SEZ", "Other"]}
                                            />

                                            <FormField
                                                label="Filing Frequency"
                                                type="dropdown"
                                                value={gst.filingFrequency}
                                                onChange={(value) => handleGstChange(index, "filingFrequency", value)}
                                                options={["Monthly", "Quarterly", "Annually"]}
                                            />

                                            <FormField
                                                label="State"
                                                value={gst.state}
                                                onChange={(value) => handleGstChange(index, "state", value)}
                                                placeholder="Enter state"
                                            />

                                            <FormField
                                                label="Next Filing Date"
                                                type="date"
                                                value={gst.nextFilingDate}
                                                onChange={(value) => handleGstChange(index, "nextFilingDate", value)}
                                            />

                                            <FormField
                                                label="Last Filed Date"
                                                type="date"
                                                value={gst.lastFiledDate}
                                                onChange={(value) => handleGstChange(index, "lastFiledDate", value)}
                                            />

                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id={`activeStatus-${index}`}
                                                    checked={gst.activeStatus}
                                                    onChange={(e) => handleGstChange(index, "activeStatus", e.target.checked)}
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                />
                                                <label htmlFor={`activeStatus-${index}`} className="ml-2 block text-sm text-gray-900">
                                                    Active Status
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* TDS Details Section */}
                {activeSection === "tds" && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-6">TDS Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField
                                label="TAN"
                                value={formData.tdsDetails.tan}
                                onChange={(value) => handleChange("tdsDetails.tan", value)}
                                placeholder="Enter TAN number"
                            />

                            <FormField
                                label="Authorized Signatory"
                                value={formData.tdsDetails.authorizedSignatory}
                                onChange={(value) => handleChange("tdsDetails.authorizedSignatory", value)}
                                placeholder="Enter authorized signatory name"
                            />

                            <FormField
                                label="Signatory PAN"
                                type="pancard"
                                value={formData.tdsDetails.signatoryPAN}
                                onChange={(value) => handleChange("tdsDetails.signatoryPAN", value)}
                                placeholder="Enter signatory PAN"
                            />

                            <FormField
                                label="Signatory Mobile"
                                type="number"
                                value={formData.tdsDetails.signatoryMobile}
                                onChange={(value) => handleChange("tdsDetails.signatoryMobile", value)}
                                placeholder="Enter signatory mobile number"
                            />

                            <FormField
                                label="Signatory Email"
                                type="email"
                                value={formData.tdsDetails.signatoryEmail}
                                onChange={(value) => handleChange("tdsDetails.signatoryEmail", value)}
                                placeholder="Enter signatory email"
                            />
                        </div>
                    </div>
                )}

                {/* Locations Section */}
                {activeSection === "locations" && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-gray-900">Locations</h3>
                            <Button
                                onClick={addLocation}
                                icon={<Plus className="h-4 w-4" />}
                                label="Add Location"
                            />
                        </div>

                        {formData.businessInformation.locations.length === 0 ? (
                            <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg">
                                <p className="text-gray-500">No locations added yet.</p>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {formData.businessInformation.locations.map((location, index) => (
                                    <div key={location.id} className="border border-gray-200 rounded-lg p-6 relative">
                                        <button
                                            type="button"
                                            onClick={() => removeLocation(index)}
                                            className="absolute top-4 right-4 text-red-500 hover:text-red-700"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>

                                        <h4 className="text-md font-medium text-gray-900 mb-4">
                                            Location #{index + 1}
                                        </h4>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id={`isHeadOffice-${index}`}
                                                    checked={location.isHeadOffice}
                                                    onChange={(e) => handleLocationChange(index, "isHeadOffice", e.target.checked)}
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                />
                                                <label htmlFor={`isHeadOffice-${index}`} className="ml-2 block text-sm text-gray-900">
                                                    Head Office
                                                </label>
                                            </div>

                                            <div className="md:col-span-2">
                                                <FormField
                                                    label="Address"
                                                    type="description"
                                                    value={location.address}
                                                    onChange={(value) => handleLocationChange(index, "address", value)}
                                                    placeholder="Enter complete address"
                                                />
                                            </div>

                                            <FormField
                                                label="City"
                                                value={location.city}
                                                onChange={(value) => handleLocationChange(index, "city", value)}
                                                placeholder="Enter city"
                                            />

                                            <FormField
                                                label="State"
                                                value={location.state}
                                                onChange={(value) => handleLocationChange(index, "state", value)}
                                                placeholder="Enter state"
                                            />

                                            <FormField
                                                label="Pincode"
                                                type="number"
                                                value={location.pincode}
                                                onChange={(value) => handleLocationChange(index, "pincode", value)}
                                                placeholder="Enter pincode"
                                                maxLength={6}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Form Actions */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex justify-end space-x-3">
                        <Button
                            variant="outline"
                            onClick={onCancel}
                            icon={<X className="h-4 w-4" />}
                            label="Cancel"
                        />
                        <Button
                            type="submit"
                            icon={<Save className="h-4 w-4" />}
                            label={isEditing ? "Update Client" : "Create Client"}
                        />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ClientForm;